/**
 * HashtagSection.tsx
 *
 * Displays extracted hashtags from post caption as colored pills/badges.
 * Allows removing hashtags by clicking them (in edit mode).
 * Includes "Generate Hashtags" button to open hashtag generator modal.
 *
 * Features:
 * - Auto-extracts hashtags from caption using same regex as backend
 * - Color-coded hashtag badges (6 color palette rotation)
 * - Click to remove hashtag (updates caption)
 * - Shows hashtag count
 * - Generate hashtags button (opens modal)
 * - Read-only mode for display-only use cases
 * - Accessible (keyboard navigation, ARIA labels)
 *
 * Usage:
 * ```tsx
 * <HashtagSection
 *   caption={caption}
 *   onCaptionChange={setCaption}
 * />
 * ```
 */

import { useMemo, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { extractHashtags, removeHashtag, addHashtags } from '@/utils/hashtags';
import { HashtagGenerator } from './HashtagGenerator';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { Tooltip } from '@/components/ui/Tooltip';

interface HashtagSectionProps {
  /**
   * Current caption text to extract hashtags from
   */
  caption: string;

  /**
   * Callback when caption should be updated (after removing hashtag or adding new ones)
   */
  onCaptionChange?: (newCaption: string) => void;

  /**
   * Read-only mode - prevents removing hashtags and hides generate button
   */
  readOnly?: boolean;

  /**
   * Custom class name for container
   */
  className?: string;
}

/**
 * HashtagSection Component
 * Shows extracted hashtags as colored badges with optional removal and generation
 */
export const HashtagSection = ({
  caption,
  onCaptionChange,
  readOnly = false,
  className = '',
}: HashtagSectionProps): JSX.Element => {
  const { t } = useTranslation('common');
  // Extract hashtags from caption (memoized to prevent recalculation)
  const hashtags = useMemo(() => extractHashtags(caption), [caption]);

  // State for hashtag generator modal
  const [generatorOpen, setGeneratorOpen] = useState(false);

  /**
   * Handle removing a hashtag
   */
  const handleRemove = (tag: string) => {
    if (onCaptionChange && !readOnly) {
      const newCaption = removeHashtag(caption, tag);
      onCaptionChange(newCaption);
    }
  };

  /**
   * Handle keyboard interaction for hashtag removal
   */
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLSpanElement>,
    tag: string
  ) => {
    // Only handle in non-readonly mode
    if (readOnly) return;

    // Remove on Enter or Space
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleRemove(tag);
    }
  };

  /**
   * Handle adding generated hashtags to caption
   */
  const handleHashtagsAdded = (newHashtags: string[]) => {
    if (onCaptionChange) {
      const updatedCaption = addHashtags(caption, newHashtags);
      onCaptionChange(updatedCaption);
    }
  };

  const VISIBLE_COUNT = 8;
  const [showAll, setShowAll] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);

  // Color palette for hashtag badges (Tailwind classes)
  const colors = [
    'bg-soft-mint text-dark-teal hover:bg-soft-mint/70',
    'bg-peach text-bs-dark hover:bg-peach/70',
    'bg-soft-coral text-bs-dark hover:bg-soft-coral/70',
    'bg-warm-gray text-bs-dark hover:bg-warm-gray/70',
    'bg-off-white text-bs-gray-stone hover:bg-light-teal-gray/30',
    'bg-light-teal-gray/30 text-dark-teal hover:bg-light-teal-gray/50',
  ];

  return (
    <>
      {/* Hashtag Generator Modal */}
      <HashtagGenerator
        open={generatorOpen}
        onClose={() => setGeneratorOpen(false)}
        onHashtagsAdded={handleHashtagsAdded}
        nested={true}
      />

      <div className={className}>
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-center gap-2">
            {hashtags.length > 0 && (
              <span className="text-xs text-bs-gray">
                {hashtags.length} {hashtags.length === 1 ? 'hashtag' : 'hashtags'}
              </span>
            )}
          </div>

          <div className="flex items-center gap-3">
            {!readOnly && hashtags.length > 0 && (
              <span className="text-xs text-bs-gray">{t('hashtag_section_click_to_remove')}</span>
            )}
            {!readOnly && (
              <button
                type="button"
                onClick={() => setGeneratorOpen(true)}
                className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-dark-teal hover:text-bs-black hover:bg-soft-mint rounded-md transition-colors focus:outline-none"
                aria-label="Generate hashtags"
              >
                <Icon name={ICONS.SPARK} size="xs" />
                {t('hashtag_section_generate')}
              </button>
            )}
          </div>
        </div>

        {/* Hashtag Badges */}
        {hashtags.length > 0 && (
          <>
            <div
              ref={scrollRef}
              className={showAll && hashtags.length > VISIBLE_COUNT ? 'max-h-[120px] overflow-y-auto scrollbar-thin' : ''}
            >
              <div className="flex flex-wrap gap-1.5">
                {(showAll ? hashtags : hashtags.slice(0, VISIBLE_COUNT)).map((tag, index) => {
                  const colorClass = colors[index % colors.length];
                  const isClickable = !readOnly;

                  return (
                    <Tooltip
                      key={tag}
                      content={t('hashtag_section_click_to_remove')}
                      disabled={!isClickable}
                    >
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium transition-colors ${colorClass} ${
                          isClickable ? 'cursor-pointer' : 'cursor-default'
                        }`}
                        onClick={isClickable ? () => handleRemove(tag) : undefined}
                        onKeyDown={isClickable ? (e) => handleKeyDown(e, tag) : undefined}
                        role={isClickable ? 'button' : undefined}
                        tabIndex={isClickable ? 0 : undefined}
                        aria-label={
                          isClickable ? `Remove hashtag ${tag}` : `Hashtag ${tag}`
                        }
                      >
                        #{tag}
                        {isClickable && (
                          <svg
                            className="ml-0.5 w-2.5 h-2.5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                            aria-hidden="true"
                          >
                            <path
                              fillRule="evenodd"
                              d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </span>
                    </Tooltip>
                  );
                })}
              </div>
            </div>
            {hashtags.length > VISIBLE_COUNT && (
              <button
                type="button"
                onClick={() => setShowAll((prev) => !prev)}
                className="mt-1.5 text-xs text-primary-500 hover:text-primary-700 font-medium transition-colors"
              >
                {showAll
                  ? t('show_less', { defaultValue: 'Show less' })
                  : t('show_more_count', { defaultValue: 'Show {{count}} more', count: hashtags.length - VISIBLE_COUNT })}
              </button>
            )}
          </>
        )}

        {/* Empty State (no hashtags yet) */}
        {hashtags.length === 0 && !readOnly && (
          <p className="text-xs text-bs-gray text-center py-2">
            {t('hashtag_section_empty_state')}
          </p>
        )}
      </div>
    </>
  );
};

export default HashtagSection;
